import { useTranslation } from 'react-i18next';
import { Menu, Moon, Sun, Monitor } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Breadcrumb } from './Breadcrumb';
import { CommandPalette } from './CommandPalette';
import { EmergencyHaltButton } from './EmergencyHaltButton';
import { EnvChip } from './EnvChip';
import { NotificationCenter } from './NotificationCenter';
import { ShortcutsDialog } from './ShortcutsDialog';
import { useThemeStore, type ThemeMode } from '@/shared/store/theme-store';

interface HeaderProps {
  onToggleSidebar?: () => void;
}

const THEME_CYCLE: ThemeMode[] = ['dark', 'light', 'system'];
const THEME_ICON = { dark: Moon, light: Sun, system: Monitor } as const;

export function Header({ onToggleSidebar }: HeaderProps) {
  const { t } = useTranslation('common');
  const themeMode = useThemeStore((s) => s.mode);
  const setThemeMode = useThemeStore((s) => s.setMode);
  const ThemeIcon = THEME_ICON[themeMode];

  return (
    <header className="flex items-center h-14 px-4 border-b border-border bg-background/95 backdrop-blur">
      {onToggleSidebar && (
        <Button variant="ghost" size="icon" onClick={onToggleSidebar} className="mr-2 xl:hidden">
          <Menu className="h-4 w-4" />
          <span className="sr-only">{t('toggleSidebar')}</span>
        </Button>
      )}

      <Breadcrumb />

      <div className="flex-1" />

      <div className="flex items-center gap-2">
        <CommandPalette />
        <EnvChip />
        <NotificationCenter />
        <ShortcutsDialog />
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => {
            const next = THEME_CYCLE[(THEME_CYCLE.indexOf(themeMode) + 1) % THEME_CYCLE.length];
            if (next) setThemeMode(next);
          }}
          title={`Theme: ${themeMode}`}
        >
          <ThemeIcon className="h-4 w-4" />
        </Button>
        <EmergencyHaltButton />
      </div>
    </header>
  );
}
